/**
 * Moteur de vol commun au joueur, aux ennemis et au boss.
 *
 * Reproduit le comportement Unreal du joueur (SpaceShipPawn::UpdateFlightFeedback + UFloatingPawnMovement) :
 * enveloppes croisière / boost / recul, frein continu, retour dans l'enveloppe, amortissement au relâchement
 * et « turning boost » qui réoriente la vitesse vers la poussée sans augmenter sa norme.
 */
import type { FlightData, ShipProfile } from '../../data';
import { DEG, Quat, Vec3 } from '../core/math';
import type { MotorProfile, Ship, ShipCommand } from '../entities';

export function playerMotorProfile(flight: FlightData, ship: ShipProfile): MotorProfile {
  const look = flight.gamepadLookRateDeg * DEG;
  return {
    cruiseMaxSpeed: ship.cruiseMaxSpeed,
    boostMaxSpeed: ship.boostMaxSpeed,
    reverseMaxSpeed: ship.reverseMaxSpeed,
    cruiseAcceleration: ship.cruiseAcceleration,
    boostAcceleration: ship.boostAcceleration,
    reverseAcceleration: ship.reverseAcceleration,
    lateralThrustScale: ship.lateralThrustScale,
    verticalThrustScale: ship.verticalThrustScale,
    maxRates: new Vec3(look * ship.pitchScale, look * ship.yawScale, flight.rollRateDeg * DEG * ship.rollScale),
    maxTotalRate: Infinity,
    releaseDeceleration: flight.releaseDeceleration,
    brakeDeceleration: flight.brakeDeceleration,
    brakeThrustFactor: flight.brakeThrustFactor,
    forwardOvershootDeceleration: flight.forwardOvershootDeceleration,
    reverseOvershootDeceleration: flight.reverseOvershootDeceleration,
    reverseSpeedThreshold: flight.reverseSpeedThreshold,
    boostInputThreshold: flight.boostInputThreshold,
    reverseInputThreshold: flight.reverseInputThreshold,
    turningBoost: flight.turningBoost,
    angularAcceleration: flight.angularAccelerationDeg * DEG,
    boostCapacity: flight.boostEnergy.capacity,
    boostDrain: flight.boostEnergy.drain,
    boostRegen: flight.boostEnergy.regen,
    boostRegenDelay: flight.boostEnergy.regenDelay,
    boostMinToStart: flight.boostEnergy.minToStart,
  };
}

export interface AiMotorParams {
  maxSpeed: number;
  acceleration: number;
  turnRateDeg: number;
  releaseDeceleration: number;
  brakeDeceleration: number;
  turningBoost: number;
}

/** Profil moteur d'un ennemi : pas de boost, recul autorisé à vitesse égale, rotation totale plafonnée. */
export function aiMotorProfile(p: AiMotorParams): MotorProfile {
  const rate = p.turnRateDeg * DEG;
  return {
    cruiseMaxSpeed: p.maxSpeed,
    boostMaxSpeed: p.maxSpeed,
    reverseMaxSpeed: p.maxSpeed,
    cruiseAcceleration: p.acceleration,
    boostAcceleration: p.acceleration,
    reverseAcceleration: p.acceleration,
    lateralThrustScale: 1,
    verticalThrustScale: 1,
    maxRates: new Vec3(rate, rate, rate),
    maxTotalRate: rate,
    releaseDeceleration: p.releaseDeceleration,
    brakeDeceleration: p.brakeDeceleration,
    brakeThrustFactor: 0,
    forwardOvershootDeceleration: Math.max(p.acceleration * 2, 20),
    reverseOvershootDeceleration: Math.max(p.acceleration * 2, 20),
    reverseSpeedThreshold: 3,
    boostInputThreshold: 0.1,
    reverseInputThreshold: 0.1,
    turningBoost: p.turningBoost,
    angularAcceleration: Infinity,
    boostCapacity: Infinity,
    boostDrain: 0,
    boostRegen: 0,
    boostRegenDelay: 0,
    boostMinToStart: 0,
  };
}

const OVER_VELOCITY_PERCENT = 1.01; // UMovementComponent::IsExceedingMaxSpeed

/**
 * Applique la rotation : consigne `turnRate` plafonnée par le profil et soumise à l'inertie,
 * plus l'impulsion de souris directe, immédiate et hors inertie.
 */
export function applyRotation(ship: Ship, command: ShipCommand, dt: number): void {
  const motor = ship.motor;
  const cap = (v: Vec3) => {
    let r = new Vec3(
      Math.max(-motor.maxRates.x, Math.min(motor.maxRates.x, v.x)),
      Math.max(-motor.maxRates.y, Math.min(motor.maxRates.y, v.y)),
      Math.max(-motor.maxRates.z, Math.min(motor.maxRates.z, v.z)),
    );
    const total = r.length();
    if (total > motor.maxTotalRate) r = r.scale(motor.maxTotalRate / total);
    return r;
  };
  let rate = cap(command.turnRate);
  // Inertie de rotation : la vitesse angulaire rejoint la consigne à accélération bornée.
  if (Number.isFinite(motor.angularAcceleration)) {
    const step = motor.angularAcceleration * dt;
    const w = ship.turnState;
    const toward = (current: number, target: number) => current + Math.max(-step, Math.min(step, target - current));
    rate = new Vec3(toward(w.x, rate.x), toward(w.y, rate.y), toward(w.z, rate.z));
  }
  ship.turnState.copy(rate);
  const impulse = command.turnImpulse;
  // Vitesse visible (inclinaison des coques) : consigne + impulsion, bornée au profil.
  ship.angularVelocity.copy(cap(rate.add(impulse.scale(1 / dt))));
  const rotation = rate.scale(dt).add(impulse);
  const angle = rotation.length();
  if (angle > 1e-9) {
    ship.rotation = ship.rotation.multiply(Quat.fromAxisAngle(rotation, angle)).normalized();
  }
}

/** Met à jour l'enveloppe et la vitesse ; ne déplace pas le vaisseau (voir Simulation.moveShip). */
export function applyThrust(ship: Ship, command: ShipCommand, dt: number): void {
  const m = ship.motor;
  const flight = ship.flight;
  const forward = ship.forward();
  const forwardInput = Math.max(-1, Math.min(1, command.thrust.z));
  let velocity = ship.velocity.clone();

  const signedForward = velocity.dot(forward);
  const reversing = forwardInput < -m.reverseInputThreshold || signedForward < -m.reverseSpeedThreshold;
  const braking = command.brake;
  const wantsBoost = command.boost && !braking && forwardInput > m.boostInputThreshold && !reversing;
  const boosting = wantsBoost && !ship.boostExhausted && ship.boostEnergy > 0;
  updateBoostEnergy(ship, boosting, dt);
  const activeMax = boosting ? m.boostMaxSpeed : reversing ? m.reverseMaxSpeed : m.cruiseMaxSpeed;

  if (braking) {
    const speed = velocity.length();
    velocity = velocity.normalized().scale(Math.max(0, speed - m.brakeDeceleration * dt));
  } else {
    const speed = velocity.length();
    if (speed > activeMax) {
      const overshoot = reversing ? m.reverseOvershootDeceleration : m.forwardOvershootDeceleration;
      velocity = velocity.normalized().scale(Math.max(activeMax, speed - overshoot * dt));
    }
  }

  const acceleration = braking
    ? m.cruiseAcceleration * m.brakeThrustFactor
    : boosting
      ? m.boostAcceleration
      : reversing
        ? m.reverseAcceleration
        : m.cruiseAcceleration;

  // UFloatingPawnMovement::ApplyControlInputToVelocity
  const control = ship.right().scale(command.thrust.x * m.lateralThrustScale)
    .add(ship.up().scale(command.thrust.y * m.verticalThrustScale))
    .add(forward.scale(forwardInput))
    .clampedLength(1);
  const analog = control.length();
  const maxPawnSpeed = activeMax * analog;
  const exceeding = velocity.lengthSq() > (maxPawnSpeed * OVER_VELOCITY_PERCENT) ** 2;

  if (analog > 0 && !exceeding) {
    if (velocity.lengthSq() > 0) {
      const timeScale = Math.max(0, Math.min(1, dt * m.turningBoost));
      // Écart volontaire à Unreal : direction normalisée. Avec une commande partielle (accélérateur, manette),
      // la formule d'origine réduisait la vitesse à chaque tick au lieu de seulement la réorienter.
      velocity = velocity.add(control.normalized().scale(velocity.length()).sub(velocity).scale(timeScale));
    }
  } else if (velocity.lengthSq() > 0) {
    const old = velocity;
    const size = Math.max(0, velocity.length() - Math.abs(m.releaseDeceleration) * dt);
    velocity = velocity.normalized().scale(size);
    if (exceeding && velocity.lengthSq() < maxPawnSpeed * maxPawnSpeed) {
      velocity = old.normalized().scale(maxPawnSpeed);
    }
  }

  const newMax = velocity.lengthSq() > (maxPawnSpeed * OVER_VELOCITY_PERCENT) ** 2 ? velocity.length() : maxPawnSpeed;
  velocity = velocity.add(control.scale(Math.abs(acceleration) * dt)).clampedLength(newMax);

  ship.velocity.copy(velocity);
  flight.boosting = boosting;
  flight.braking = braking;
  flight.reversing = reversing;
  flight.forwardInput = forwardInput;
  flight.signedForwardSpeed = velocity.dot(forward);
  flight.envelope = braking ? 'brake' : boosting ? 'boost' : reversing ? 'reverse' : 'cruise';
  flight.boostElapsed = boosting ? (flight.boostElapsed < 0 ? 0 : flight.boostElapsed + dt) : -1;
}

/** Jauge de boost : se vide en boost, se recharge après un court délai ; vide = coupé jusqu'au seuil. */
function updateBoostEnergy(ship: Ship, boosting: boolean, dt: number): void {
  const m = ship.motor;
  if (!Number.isFinite(m.boostCapacity)) return;
  if (boosting) {
    ship.boostEnergy = Math.max(0, ship.boostEnergy - m.boostDrain * dt);
    ship.boostRegenTimer = m.boostRegenDelay;
    if (ship.boostEnergy <= 0) ship.boostExhausted = true;
  } else {
    ship.boostRegenTimer = Math.max(0, ship.boostRegenTimer - dt);
    if (ship.boostRegenTimer <= 0) ship.boostEnergy = Math.min(m.boostCapacity, ship.boostEnergy + m.boostRegen * dt);
    if (ship.boostExhausted && ship.boostEnergy >= m.boostMinToStart * m.boostCapacity) ship.boostExhausted = false;
  }
}

/**
 * Vitesse angulaire locale qui oriente le vaisseau vers `target` en un tick si possible,
 * sinon au taux maximal (équivalent du Slerp borné d'AEnemyDrone::RotateTowards).
 */
export function turnRateToward(current: Quat, target: Quat, maxRate: number, dt: number): Vec3 {
  const delta = current.conjugate().multiply(target);
  const rotation = delta.toRotationVector();
  const angle = rotation.length();
  if (angle < 1e-6 || dt <= 0) return new Vec3();
  const rate = Math.min(angle / dt, maxRate);
  return rotation.scale(rate / angle);
}
